import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { getMeTenantPermissionsQueryOptions, TENANT_CONTEXT_STALE_TIME } from "api/User/queryOptions";
import { reportErrorToFlare, toError } from "components/UI/ErrorBoundary/utils";
import { PermissionEnum } from "types/enums";

import { useUser } from "./useUser";

type TenantPermissionCheck = "granted" | "denied" | "failed";

interface PermissionInAnyTenantResult {
    hasPermission: boolean;
    /** At least one tenant could not be checked, so a negative result may be wrong. */
    isDegraded: boolean;
}

/**
 * Checks whether the current user has the permission in at least one tenant they are an active member of.
 *
 * A failing per-tenant permissions request (e.g. 403 for a workspace the user can no longer access)
 * (or a malformed response) is reported to Flare without a toast and treated as "no permissions" —
 * it must not take down the whole page. A negative result built on such a failure is never cached
 * as fresh, so the check is retried on the next visit instead of hiding the feature for the full stale time.
 */
export function usePermissionInAnyTenant(permission: PermissionEnum): boolean {
    const queryClient = useQueryClient();
    const tenantSlugs = (useUser().getAuthenticatedUser()?.tenants ?? [])
        .filter((tenant) => tenant.user_active !== false)
        .map((tenant) => tenant.slug_name);

    const { data } = useSuspenseQuery({
        queryKey: ["me", "permissionInAnyTenant", permission, tenantSlugs],
        queryFn: async (): Promise<PermissionInAnyTenantResult> => {
            const checkTenant = async (tenantSlug: string): Promise<TenantPermissionCheck> => {
                try {
                    const { permissions } = await queryClient.fetchQuery(
                        getMeTenantPermissionsQueryOptions(tenantSlug, true)
                    );

                    return permissions.some((tenantPermission) => tenantPermission.name === permission)
                        ? "granted"
                        : "denied";
                } catch (error) {
                    reportErrorToFlare(toError(error), "usePermissionInAnyTenant");
                    return "failed";
                }
            };

            const results = await Promise.all(tenantSlugs.map(checkTenant));

            return {
                hasPermission: results.includes("granted"),
                isDegraded: results.includes("failed"),
            };
        },
        staleTime: (query) =>
            query.state.data?.isDegraded && !query.state.data.hasPermission ? 0 : TENANT_CONTEXT_STALE_TIME,
    });

    return data.hasPermission;
}
